import { API_CONFIG, STORAGE_KEYS } from './constants';
import { getAttributionSummary } from './utm';

export interface FunnelEvent {
  event_type: string;
  page_url: string;
  element_id?: string;
  course_id?: number;
  metadata?: Record<string, any>;
}

const BATCH_SIZE = 10;
const FLUSH_INTERVAL = 5000;
let eventQueue: FunnelEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function getUserId(): number | null {
  try {
    const session = localStorage.getItem(STORAGE_KEYS.SESSION);
    if (session) {
      const parsed = JSON.parse(session);
      return parsed?.user?.id ?? null;
    }
  } catch {
    // ignore
  }
  return null;
}

function getToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
}

async function sendEvents(events: FunnelEvent[]): Promise<void> {
  const attr = getAttributionSummary();
  const payload = events.map((evt) => ({
    ...evt,
    session_id: attr.sessionId,
    user_id: getUserId(),
    utm_source: attr.utm.utm_source ?? '',
    utm_medium: attr.utm.utm_medium ?? '',
    utm_campaign: attr.utm.utm_campaign ?? '',
    gclid: attr.utm.gclid ?? '',
    fbclid: attr.utm.fbclid ?? '',
    wbraid: attr.utm.wbraid ?? '',
    whatsapp_ref: attr.utm.ref ?? '',
    device_type: attr.deviceType,
  }));

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(`${API_CONFIG.BASE_URL}/funnel/events`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events: payload }),
      keepalive: true,
    });
  } catch (err) {
    // Silently fail — funnel tracking should never break the user experience
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Funnel] Failed to send events:', err);
    }
  }
}

function flush(): void {
  if (eventQueue.length === 0) return;
  const batch = eventQueue.splice(0, BATCH_SIZE);
  sendEvents(batch);
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flush();
    flushTimer = null;
  }, FLUSH_INTERVAL);
}

export function trackFunnelEvent(event: FunnelEvent): void {
  eventQueue.push(event);
  if (eventQueue.length >= BATCH_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
}

// Flush remaining events before page unload
window.addEventListener('beforeunload', () => {
  if (eventQueue.length > 0) {
    sendEvents(eventQueue);
  }
});

// Convenience wrappers for common events
export function trackPageView(pagePath?: string): void {
  trackFunnelEvent({
    event_type: 'page_view',
    page_url: pagePath ?? window.location.href,
  });
}

export function trackCtaClick(elementId: string, courseId?: number, metadata?: Record<string, any>): void {
  trackFunnelEvent({
    event_type: 'cta_click',
    page_url: window.location.href,
    element_id: elementId,
    course_id: courseId,
    metadata,
  });
}

export function trackLeadCapture(source: string, metadata?: Record<string, any>): void {
  trackFunnelEvent({
    event_type: 'lead_capture_success',
    page_url: window.location.href,
    metadata: { source, ...metadata },
  });
}

export function trackEnrollmentStart(courseId: number, metadata?: Record<string, any>): void {
  trackFunnelEvent({
    event_type: 'enrollment_start',
    page_url: window.location.href,
    course_id: courseId,
    metadata,
  });
}

export function trackEnrollmentStep(step: number, courseId?: number): void {
  trackFunnelEvent({
    event_type: 'enrollment_step_complete',
    page_url: window.location.href,
    course_id: courseId,
    metadata: { step },
  });
}

export function trackCheckoutInitiated(courseId: number, amount: number, metadata?: Record<string, any>): void {
  trackFunnelEvent({
    event_type: 'checkout_initiated',
    page_url: window.location.href,
    course_id: courseId,
    metadata: { amount, ...metadata },
  });
}

export function trackPaymentSuccess(courseId: number, amount: number, orderId: string): void {
  trackFunnelEvent({
    event_type: 'payment_success',
    page_url: window.location.href,
    course_id: courseId,
    metadata: { amount, order_id: orderId },
  });
}

export function trackPaymentFailed(courseId: number, amount: number, reason?: string): void {
  trackFunnelEvent({
    event_type: 'payment_failed',
    page_url: window.location.href,
    course_id: courseId,
    metadata: { amount, reason },
  });
}

export function trackCheckoutAbandoned(courseId: number, amount: number, metadata?: Record<string, any>): void {
  trackFunnelEvent({
    event_type: 'checkout_abandoned',
    page_url: window.location.href,
    course_id: courseId,
    metadata: { amount, ...metadata },
  });
}

export function trackScrollDepth(depthPercent: number): void {
  trackFunnelEvent({
    event_type: `scroll_depth_${depthPercent}`,
    page_url: window.location.href,
  });
}

export function trackSectionView(sectionId: string, courseId?: number): void {
  trackFunnelEvent({
    event_type: 'section_view',
    page_url: window.location.href,
    element_id: sectionId,
    course_id: courseId,
  });
}

export function trackVideoPlay(videoId: string, courseId?: number): void {
  trackFunnelEvent({
    event_type: 'video_play',
    page_url: window.location.href,
    element_id: videoId,
    course_id: courseId,
  });
}

export function trackFaqExpand(questionId: string, courseId?: number): void {
  trackFunnelEvent({
    event_type: 'faq_expand',
    page_url: window.location.href,
    element_id: questionId,
    course_id: courseId,
  });
}

export function trackCouponApplied(code: string, discount: number, courseId?: number): void {
  trackFunnelEvent({
    event_type: 'coupon_applied',
    page_url: window.location.href,
    course_id: courseId,
    metadata: { code, discount },
  });
}

export function trackWhatsAppClick(ref: string): void {
  trackFunnelEvent({
    event_type: 'whatsapp_click',
    page_url: window.location.href,
    metadata: { ref },
  });
}
